/**
 * @voltxampere/web — Playback speed control (Sprint 53)
 *
 * VISION §6.E (Live Canvas & Game Feel) P0 Faz 1 — vision audit Item
 * 19. Toolbar speed dropdown 0.1× / 0.25× / 0.5× / 1× / 2× / 5× / 10×
 * applied as a multiplier on `runTransient`'s effective tstop at the
 * runner-store boundary (Sprint 15 transient-runner.ts). Engine zero-
 * diff invariant preserved — engine receives a pre-scaled tstop and
 * sees no speed concept.
 *
 * Default 1 (real-time). Persisted to IDB SettingsRecord.playback.
 * defaultSpeed; boot hydrate restores the user's last preset.
 *
 * Bookmark integration (Sprint 32-3 bookmarks.ts) — `addBookmark`
 * snapshots `playbackSpeedSignal.value` into the bookmark; jumping to
 * a bookmark restores the speed via `restorePlaybackSpeedFromBookmark`.
 *
 * Pattern mirrors `damage-prefs.ts` (Sprint 36-5) + `thermal-prefs.ts`
 * (Sprint 34-5).
 *
 * Copyright © 2026 360 Studio
 * Proprietary — All rights reserved.
 */

import { signal } from "@preact/signals-core";
import { getSettings, mergeSettings } from "@voltxampere/persistence";

/**
 * Canonical speed presets shown in the toolbar dropdown + AYARLAR
 * Simülasyon default-speed entry. Sorted ascending so dropdown menus
 * can iterate in order. 1 (real-time) is at index 3.
 */
export const SPEED_PRESETS: readonly number[] = [0.1, 0.25, 0.5, 1, 2, 5, 10];

/** Real-time (1×) — used as default + neutral element. */
export const DEFAULT_PLAYBACK_SPEED = 1;

/**
 * Active simulation speed multiplier. Applied to `runTransient`'s
 * effective tstop. The transient-runner consumer reads this once per
 * call and multiplies; the engine itself never sees the speed concept.
 */
export const playbackSpeedSignal = signal<number>(DEFAULT_PLAYBACK_SPEED);

let initialised = false;

/**
 * Snap an arbitrary numeric speed to the nearest SPEED_PRESETS entry.
 * Defends against persisted custom values from future versions /
 * tampered IDB.
 */
export function snapSpeedToPreset(speed: number): number {
  if (!Number.isFinite(speed) || speed <= 0) return DEFAULT_PLAYBACK_SPEED;
  let nearest = SPEED_PRESETS[0] ?? DEFAULT_PLAYBACK_SPEED;
  let nearestDistance = Math.abs(speed - nearest);
  for (const preset of SPEED_PRESETS) {
    const distance = Math.abs(speed - preset);
    if (distance < nearestDistance) {
      nearest = preset;
      nearestDistance = distance;
    }
  }
  return nearest;
}

/**
 * Hydrate the speed signal from IDB. Called once at boot from
 * main.ts. Returns the hydrated value (or DEFAULT_PLAYBACK_SPEED if
 * no setting was persisted).
 *
 * Defensive: invalid types / non-finite numbers / values outside the
 * preset set are snapped to the nearest preset.
 */
export async function initPlaybackSpeed(): Promise<number> {
  if (initialised) return playbackSpeedSignal.value;
  initialised = true;
  try {
    const settings = await getSettings();
    const stored = settings?.playback?.defaultSpeed;
    if (typeof stored === "number" && Number.isFinite(stored) && stored > 0) {
      playbackSpeedSignal.value = snapSpeedToPreset(stored);
    }
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn("[VXA playback-speed] hydrate failed:", err);
    }
  }
  return playbackSpeedSignal.value;
}

/**
 * Update the active playback speed. Snaps the input to the nearest
 * preset (callers should pass a SPEED_PRESETS value directly) and
 * fire-and-forget persists to IDB.
 */
export function setPlaybackSpeed(speed: number): void {
  const snapped = snapSpeedToPreset(speed);
  playbackSpeedSignal.value = snapped;
  void mergeSettings({
    playback: { defaultSpeed: snapped },
  }).catch((err: unknown) => {
    if (import.meta.env.DEV) {
      console.warn("[VXA playback-speed] persist failed:", err);
    }
  });
}

/**
 * Format a speed multiplier for display (e.g. "0.5×", "10×"). Strips
 * trailing ".0" so 1 renders as "1×" not "1.0×".
 */
export function formatSpeedLabel(speed: number): string {
  const rounded = Math.round(speed * 100) / 100;
  const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toString();
  return `${text}×`;
}

/** Test cleanup — reset hydration flag + signal to default. */
export function __resetPlaybackSpeedForTests(): void {
  initialised = false;
  playbackSpeedSignal.value = DEFAULT_PLAYBACK_SPEED;
}
